/**
 * Per-SOURCE rules for the tax/price display elements. These are applied IDENTICALLY on every
 * surface that can render them — listing card, detail page, guest-details sidebar, payment sidebar,
 * booking-confirmation page, booking-detail page, and emails — so a given supplier shows the SAME
 * thing everywhere. The gating is SOURCE-driven, never data-driven: e.g. RateHawk never shows a
 * price-breakdown row shape meant for another source even if its data happens to look similar.
 *
 * The "Includes X% VAT - {amount}" line and the standalone "(Includes taxes and charges)" line have
 * been REMOVED from the UI entirely (product — VAT line 2026-06-30, charges line once the itemised
 * checkout breakdown below took over showing every source's tax component explicitly); there is no
 * gating function for either any more — the underlying VAT data is retained for the CMS grids /
 * partner portal, but nothing renders those two lines anywhere. Likewise the itemised price
 * breakdown is unconditional now (shown for every source), so there is no gate for it either — only
 * its row SHAPE is still source-driven, via `usesExTaxBreakdown` below.
 *
 * `source` is the cart/record/detail source string ('BookingClap' | 'RateHawk' | 'HyperGuest' |
 * 'WebHotelier'); the RateHawk CUG feed ('ratehawk-cug') counts as RateHawk. Case-insensitive.
 */
function normSource(source?: string | null): string {
  return (source || '').trim().toLowerCase();
}

/** Tax-list (i) tooltip — RateHawk (incl. CUG) + HyperGuest only. VAT is excluded from the list separately. */
export function showsTaxList(source?: string | null): boolean {
  const s = normSource(source);
  return s === 'ratehawk' || s === 'ratehawk-cug' || s === 'hyperguest';
}

/**
 * Selects the checkout breakdown's row SHAPE — extranet (BookingClap) is the only source with a
 * genuine ex-VAT/VAT split, so it alone gets the 4-row ex-tax shape (Price ex tax / Transaction fee
 * ex tax / Taxes / Total). Every other source (RateHawk, HyperGuest, WebHotelier) quotes an
 * all-inclusive gross price and gets the 3-row incl-tax shape (Price incl tax / Transaction fee incl
 * tax / Total) — see `buildPriceBreakdownRows`.
 */
export function usesExTaxBreakdown(source?: string | null): boolean {
  return normSource(source) === 'bookingclap';
}

/** One labelled row of the checkout price breakdown, in display order. */
export interface PriceBreakdownRow {
  label: 'Price' | 'Transaction fee' | 'Taxes' | 'Total Price';
  /** Property-currency amount — the caller formats/converts for display. */
  amount: number;
}

/**
 * Structural shape of `CartData.priceBreakdown` (see `@bip/api-client` `CartPriceBreakdown` for the
 * full documented type) — declared locally so `@bip/shared` doesn't take a dependency on
 * `@bip/api-client` for one type. Field names are PascalCase because `getCart` returns the raw
 * Mongoose doc verbatim (no camelCase transform) — same convention as every other cart ledger field.
 * Carries BOTH the property-currency fields and their `*InUserCurrency` twins so
 * `buildPriceBreakdownRows` can pick ONE currency consistently (see its `preferUserCurrency` param) —
 * mixing the two (property-currency rows under a user-currency Total) was the pre-existing bug this
 * replaces (`BookingSidebar`'s old `PriceBreakdownRows` read `fr.totalPriceExTax`/`totalTax` in
 * property currency while "Total Price" beside it showed the user-currency `displayTotal`).
 */
export interface PriceBreakdownSource {
  PriceWithBIPCommissionExTax: number;
  PriceWithBIPCommissionExTaxInUserCurrency?: number;
  Taxes: number;
  TaxesInUserCurrency?: number;
  PriceWithBIPCommissionInclTax: number;
  PriceWithBIPCommissionInclTaxInUserCurrency?: number;
  CostCommissionExVat: number;
  CostCommissionExVatInUserCurrency?: number;
  CostCommissionAmount: number;
  CostCommissionAmountInUserCurrency?: number;
  FinalPriceOnWebsite: number;
  FinalPriceOnWebsiteInUserCurrency?: number;
}

/**
 * Builds the checkout price-breakdown rows for a cart/source, per `usesExTaxBreakdown`'s shape
 * selection. The Transaction fee row is ALWAYS emitted, even at 0.00 (fixed breakdown shape — a
 * commission-bearing source with a genuine 0% rate is a real, expected case). `Total Price` is
 * always the SUM of the rows above it (matches the story's "addition of all N above"), which equals
 * `breakdown.FinalPriceOnWebsite` whenever the source is commission-bearing; for a source outside
 * `chargesCommission()` (never a live source in production — see `hotels-service` `commission.ts`)
 * `calculateAllCommissions` leaves every field at 0, so this falls back to `fallbackTotal`
 * (`cart.totalPrice`/`cart.totalPriceInUserCurrency`, in the SAME currency as `preferUserCurrency`)
 * rather than rendering "Total Price: 0.00".
 *
 * `preferUserCurrency`: pass `true` when the surface displays in the user's converted currency (every
 * funnel surface that shows `cart.totalPriceInUserCurrency`) so every row — not just the Total —
 * converts consistently; `false` only for a property-currency-only rendering (e.g. an email's
 * property-recipient copy). Falls back to the property-currency field when the `*InUserCurrency`
 * twin is absent (a legacy cart persisted before it existed).
 */
export function buildPriceBreakdownRows(
  breakdown: PriceBreakdownSource | undefined,
  source: string | null | undefined,
  fallbackTotal: number,
  preferUserCurrency = true,
): PriceBreakdownRow[] {
  const pick = (propertyValue: number, userValue: number | undefined): number =>
    preferUserCurrency && userValue !== undefined ? userValue : propertyValue;

  if (!breakdown || breakdown.FinalPriceOnWebsite === 0) {
    return [{ label: 'Total Price', amount: fallbackTotal }];
  }
  if (usesExTaxBreakdown(source)) {
    const rows: PriceBreakdownRow[] = [
      { label: 'Price', amount: pick(breakdown.PriceWithBIPCommissionExTax, breakdown.PriceWithBIPCommissionExTaxInUserCurrency) },
      { label: 'Transaction fee', amount: pick(breakdown.CostCommissionExVat, breakdown.CostCommissionExVatInUserCurrency) },
      { label: 'Taxes', amount: pick(breakdown.Taxes, breakdown.TaxesInUserCurrency) },
    ];
    const total = rows.reduce((sum, r) => sum + r.amount, 0);
    return [...rows, { label: 'Total Price', amount: total }];
  }
  const rows: PriceBreakdownRow[] = [
    { label: 'Price', amount: pick(breakdown.PriceWithBIPCommissionInclTax, breakdown.PriceWithBIPCommissionInclTaxInUserCurrency) },
    { label: 'Transaction fee', amount: pick(breakdown.CostCommissionAmount, breakdown.CostCommissionAmountInUserCurrency) },
  ];
  const total = rows.reduce((sum, r) => sum + r.amount, 0);
  return [...rows, { label: 'Total Price', amount: total }];
}
